import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Checkbox } from '@telus-uds/components-web';

import Note from '../../note';
import FieldGuide from './common/fieldGuide';
import { fieldYesToAllAdvisories } from '../../utils/constant';
import formFieldOperationOptions from '../../utils/formFieldOperationOptions';

// UDS Checkbox's `tokens.labelFontWeight` routes through a fontName+fontWeight
// font-family lookup (see @telus-uds/components-base applyTextStyles) that
// this app doesn't register a matching @font-face for, so it silently has no
// visual effect. Force the bold weight directly on the rendered <label> via
// CSS instead. `!important` is required because src/assets/css/style.css has
// a global `[role="checkbox"] label { font-weight: 400 !important; }` reset.
const CheckboxWrapper = styled.div`
  ${({ $bold }) =>
    $bold &&
    `
      label {
        font-weight: bold !important;
      }
    `}
`;

const FieldCheckbox = ({ data }) => {
  const {
    checked,
    name,
    label,
    value,
    onChange,
    readonly,
    tooltip,
    tooltipLong,
    toolTipRichText,
    note,
    noteType,
    note2,
    note2Type,
    isShowNoteBackground,
    isLabelBold
  } = data;
  const customStyle =
    name === fieldYesToAllAdvisories
      ? {
          marginBottom: '1rem',
          paddingBottom: '1rem',
          borderBottom: '1px solid #D8D8D8',
          fontWeight: '500'
        }
      : {
          display: 'flex'
        };
  const tooltipMessage = tooltip || tooltipLong || toolTipRichText;

  const renderNotes = (noteField, variant) => {
    return (
      noteField &&
      noteField.content && (
        <Note
          variant={variant}
          type="richText"
          options={formFieldOperationOptions(value)}
          data={noteField}
          isShowNoteBackground={isShowNoteBackground}
        />
      )
    );
  };

  return (
    <>
      <div style={customStyle}>
        <CheckboxWrapper key={name} $bold={!!isLabelBold} style={{ display: 'flex' }}>
          <Checkbox
            label={label}
            name={name}
            value={value}
            checked={!!checked}
            onChange={() => {
              // Synthesize the exact same DOM event shape the form engine expects:
              // handleFormField destructures e.target → { name, value, checked }
              // Sign-contract files ignore the event entirely (just toggle state)
              // Mirror native <input type="checkbox">: without a value attribute
              // e.target.value is "on". An undefined value would make getFormData
              // drop the field, so the selection would never be saved.
              onChange({
                target: {
                  name,
                  value: value === undefined || value === null ? 'on' : value,
                  checked: !checked
                }
              });
            }}
            inactive={readonly}
          />
        </CheckboxWrapper>
        {tooltipMessage && (
          <FieldGuide tooltip={tooltipMessage} isRichText={toolTipRichText} />
        )}
      </div>
      {renderNotes(note, noteType)}
      {renderNotes(note2, note2Type)}
    </>
  );
};

FieldCheckbox.propTypes = {
  data: PropTypes.shape({
    checked: PropTypes.bool,
    name: PropTypes.string.isRequired,
    type: PropTypes.string,
    label: PropTypes.string,
    value: PropTypes.string,
    readonly: PropTypes.bool,
    onChange: PropTypes.func.isRequired,
    tooltip: PropTypes.string,
    tooltipLong: PropTypes.string,
    toolTipRichText: PropTypes.shape({
      content: PropTypes.arrayOf(PropTypes.object)
    }),
    note: PropTypes.shape({
      content: PropTypes.arrayOf(PropTypes.object)
    }),
    noteType: PropTypes.string,
    note2: PropTypes.shape({
      content: PropTypes.arrayOf(PropTypes.object)
    }),
    note2Type: PropTypes.string,
    isShowNoteBackground: PropTypes.bool
  }).isRequired
};

export default FieldCheckbox;
